import { useParams } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import { Box, Typography, CircularProgress, Button, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { DATA_URL } from '../config';
import { useReactToPrint } from 'react-to-print';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import '../App.css';

export const formatUTCPeriod = (period) => {
  if (!period || period.length !== 2) return 'no input';

  const formatDateUTC = (timestamp) => {
    const d = new Date(Number(timestamp));
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
  };

  return `${formatDateUTC(period[0])} - ${formatDateUTC(period[1])}`;
};

const printIframe = () => {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:absolute;width:0;height:0;border:none;';
  document.body.appendChild(iframe);

  // Clone only the content you want to print
  const printContent = document.querySelector('.printable-area').cloneNode(true);

  // Remove the "Network Event Detail" title and V-LARA logo
  const titleBox = printContent.querySelector('.events-modal-title');
  if (titleBox) titleBox.remove();

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <title>Print</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 0; padding: 20px; }
          .field-row { margin-bottom: 10px; }
          .field-row strong { display: block; font-weight: bold; }
          .field-row p { margin: 2px 0 0 15px; max-width: 600px; }
          table { border-collapse: collapse; width: 100%; max-width: 600px; }
          table, th, td { border: 1px solid black; }
          th, td { padding: 8px; text-align: left; }
          img { max-width: 100%; }
          @media print {
            body { overflow: visible; height: auto; }
            .printable-area { height: auto; page-break-inside: avoid; }
            .field-row { page-break-inside: avoid; }
            table { page-break-inside: auto; }
            tr { page-break-inside: avoid; page-break-after: auto; }
            img { page-break-inside: avoid; }
          }
        </style>
      </head>
      <body>${printContent.innerHTML}</body>
    </html>
  `);
  doc.close();
  iframe.contentWindow.focus();
  iframe.contentWindow.print();
  setTimeout(() => document.body.removeChild(iframe), 1000);
};

const EventDetail = () => {
  const { id } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hideEmpty, setHideEmpty] = useState(false);
   
  document.title = "V-LARA Event Detail";

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const res = await fetch(`${DATA_URL}getEvent/${id}`, { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to fetch event');
        const data = await res.json();
        setEvent(data);
      } catch (err) {
        console.error('Error fetching event:', err);
        setError('Failed to load event data.');
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [id]);

  if (loading) return <div style={{ textAlign: 'center', marginTop: '20px' }}><CircularProgress /></div>;
  if (error) return <div style={{ textAlign: 'center', marginTop: '20px', color: 'red' }}>{error}</div>;
  if (!event) return <div>No event found.</div>;

  const renderField = (label, value) => {
    if (!value || value === 'no input') return null;
    return (
      <div className="field-row">
        <strong>{label}:</strong>
        <div style={{ whiteSpace: 'pre-line' }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {value}
          </ReactMarkdown>
        </div>
      </div>
    );
  };

  return (
    <div className='event-detail-page printable-area' style={{ padding: '20px'}}>

      <Box className="events-modal-title" display="flex" alignItems="center" justifyContent="space-between">
        
        <Box display="flex" alignItems="center">
            <img src='/images/globe.svg' alt='Events' style={{ width : '22px', height: 'auto', marginRight: '8px'}} />
            <Typography variant="h6" sx={{display: 'inline'}}>Network Event Detail</Typography>
        </Box>
        
          <img src='/images/vlara-logo.svg' alt='Logo' style={{ height: '40px' }} />
      </Box>

      <Button size="small" onClick={printIframe}>Print</Button>
      <Button size='small' onClick={() => setHideEmpty(!hideEmpty)}>
        {hideEmpty ? 'Show empty fields' : 'Hide empty fields'}
      </Button>

      <h1 style={{marginTop: '10px'}}>{event.type} Event</h1>
      <h2>{event.name}</h2>

      {event.imageurl && event.imageurl !== 'no input' && /\.(jpg|jpeg|png)$/i.test(event.imageurl) && (
        <Box sx={{ mt: 2 }}>
          <img src={event.imageurl} alt="Event" style={{ maxWidth: '100%', width: '600px', border: '1px solid #ccc' }} />
        </Box>
      )}

      <h2>General Information</h2>
      {renderField("Type", event.type)}
      {renderField("Sub Type", event.subtype)}
      {renderField("Status", event.status)}
      {renderField("Estimated Period", formatUTCPeriod(event.estimatedPeriod))}      
      {renderField("Locations", (event.locations && event.locations.length > 0) ? event.locations.join(', ').toUpperCase() + 'FIR' : 'no input')}
      {renderField("Last Published", event.lastpublisheddate)}
      {renderField("Creator", `${event.organisationname} (${event.creator})`)}

      <h3 style={{ marginTop: '20px' }}>Event Description</h3>
      {renderField("Description", event.description)}

      <h3 style={{ marginTop: '20px' }}>Reference Publications</h3>
      {event.attachments && event.attachments.length > 0 ? (
        <Table size="small" sx={{ maxWidth: 600, borderCollapse: 'collapse' }}>
            <TableHead>
            <TableRow>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ border: '1px solid black', fontWeight: 'bold' }}>Attachment</TableCell>
            </TableRow>
            </TableHead>
            <TableBody>
            {event.attachments.map((att, i) => {
                const fileName = att.url ? att.url.split('/').pop() : '';
                return (
                <TableRow key={i}>
                    <TableCell sx={{ border: '1px solid black' }}>{att.name || 'no input'}</TableCell>
                    <TableCell sx={{ border: '1px solid black' }}>{att.type || 'no input'}</TableCell>
                    <TableCell sx={{ border: '1px solid black' }}>
                    {att.url ? (
                        <a href={att.url} target="_blank" rel="noopener noreferrer">{fileName}</a>
                    ) : 'no input'}
                    </TableCell>
                </TableRow>
                );
            })}
            </TableBody>
        </Table>
      ) : (!hideEmpty && 'no input')}

      {event.attachmentimageurl && event.attachmentimageurl !== 'no input' && /\.(jpg|jpeg|png)$/i.test(event.attachmentimageurl) && (
        <Box sx={{ mt: 2 }}>
          <img src={event.attachmentimageurl} alt="Attachment" style={{ maxWidth: '100%', width: '600px', border: '1px solid #ccc' }} />
        </Box>
      )}

      <h3 style={{ marginTop: '20px' }}>Flight Information</h3>
      {renderField("Number of Flights & Flight Level Range", event.measures)}

      <h2 style={{ marginTop: '20px' }}>Activities and Impacts</h2>
      {renderField("Activities", event.activities)}
      {renderField("Network Impact", event.impact)}
      {renderField("Impact Description", event.impactdesc)}

      <h3 style={{ marginTop: '20px' }}>Remarks and Comments</h3>
      {renderField("Originator Comments", event.originatorcomments)}
      {renderField("Network Manager Comments", event.nmcomments)}

      <h2 style={{ marginTop: '20px' }}>Originator</h2>
      {renderField("Organisation Type", event.organisationtype)}
      {renderField("Organisation Name", event.organisationname)}

      <style>{`
        .field-row {
          margin-bottom: 10px;
        }
        .field-row strong {
          display: block;
          font-weight: bold;
        }
        .field-row p {
          margin: 2px 0 0 15px;
          max-width: 600px;
        }
        @media print {
          button { display: none; }
        }
      `}</style>
    </div>
  );
};

export default EventDetail;
